import { useQuery } from "@tanstack/react-query"
import { SectionHero, StatGrid, MetricCard, AdminCard, StatusPill } from "@/components/admin/primitives"
import { Badge } from "@/components/ui/badge"
import { ActivityFeed } from "@/components/admin/ActivityFeed"
import { getQuickMetrics } from "@/lib/api/admin/metrics"
import { getRecentActivity } from "@/lib/api/admin/activity"
import { useAdminAccess } from "@/hooks/useAdminAccess"
import { Activity, BarChart3, GraduationCap, Bug, CheckCircle2, Award, Building2, Shield, Video, Brain, Zap, AlertTriangle, Users, TrendingUp } from "lucide-react"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"
import { supabase } from "@/integrations/supabase/client"

const platformAdminTools = [
  {
    path: "/admin/health",
    icon: Activity,
    title: "System Health",
    description: "Monitor system status and health checks",
    color: "success" as const,
  },
  {
    path: "/admin/ops",
    icon: BarChart3,
    title: "Operations",
    description: "Pipeline metrics and performance",
    color: "info" as const,
  },
  {
    path: "/admin/analysis-quality",
    icon: TrendingUp,
    title: "Analysis Quality",
    description: "Interpretation quality metrics and QA dashboard",
    color: "info" as const,
  },
  {
    path: "/admin/users",
    icon: Users,
    title: "User Management",
    description: "View all users, reset passwords, and manage training plans",
    color: "primary" as const,
  },
  {
    path: "/admin/pipeline",
    icon: Video,
    title: "Video Pipeline",
    description: "Monitor AI analysis and coaching pipeline",
    color: "primary" as const,
  },
  {
    path: "/admin/coaching",
    icon: Brain,
    title: "AI Coaching Agent",
    description: "Monitor coaching agent performance and errors",
    color: "info" as const,
  },
  {
    path: "/admin/learning",
    icon: GraduationCap,
    title: "Learning Stages",
    description: "Manage user learning progression",
    color: "primary" as const,
  },
  {
    path: "/admin/debug",
    icon: Bug,
    title: "Debug Tools",
    description: "Video and telemetry debugging",
    color: "warning" as const,
  },
  {
    path: "/admin/courses/claims",
    icon: CheckCircle2,
    title: "Course Claims",
    description: "Approve course management requests",
    color: "info" as const,
  },
  {
    path: "/admin/badges",
    icon: Award,
    title: "Badge Management",
    description: "Create and manage platform badges",
    color: "primary" as const,
  },
]

const courseAdminTools = [
  {
    path: "/clubhouse",
    icon: Building2,
    title: "My Courses",
    description: "Manage your course pages",
    color: "primary" as const,
  },
]

export default function AdminHub() {
  const { isPlatformAdmin, isCourseAdmin, managedCourses } = useAdminAccess()
  
  const { data: metrics, isLoading: metricsLoading, error: metricsError } = useQuery({
    queryKey: ["admin_metrics_quick"],
    queryFn: getQuickMetrics,
    refetchInterval: 30000, // Refresh every 30 seconds
    retry: false, // Don't retry if Edge Function doesn't exist
    enabled: isPlatformAdmin, // Only fetch if platform admin
  })

  // Pipeline metrics query
  const { data: pipelineMetrics, isLoading: pipelineLoading, error: pipelineError } = useQuery({
    queryKey: ["admin_pipeline_metrics"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('swing_runs')
        .select('status, analysis_stage, created_at, coaching_result, ai_result')
        .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()) // Last 24 hours
      
      if (error) throw error
      
      const total = data.length
      const completed = data.filter(r => r.status === 'ready').length
      const failed = data.filter(r => r.status === 'failed').length
      const processing = data.filter(r => r.status === 'processing').length
      const withCoaching = data.filter(r => r.coaching_result !== null).length
      const withAI = data.filter(r => r.ai_result !== null).length
      
      return {
        total,
        completed,
        failed,
        processing,
        successRate: total > 0 ? ((completed / total) * 100) : 0,
        coachingRate: total > 0 ? ((withCoaching / total) * 100) : 0,
        aiRate: total > 0 ? ((withAI / total) * 100) : 0,
      }
    },
    refetchInterval: 30000,
    enabled: isPlatformAdmin,
  })

  // Failed jobs query
  const { data: failedJobs, isLoading: failedJobsLoading } = useQuery({
    queryKey: ["admin_failed_jobs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('failed_jobs')
        .select('job_type, error_code, created_at')
        .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
        .order('created_at', { ascending: false })
        .limit(10)
      
      if (error) throw error
      return data
    },
    refetchInterval: 60000,
    enabled: isPlatformAdmin,
  })

  const { data: activity, isLoading: activityLoading, error: activityError } = useQuery({
    queryKey: ["admin_activity_recent"],
    queryFn: () => getRecentActivity(20),
    refetchInterval: 60000, // Refresh every minute
    retry: false, // Don't retry if Edge Function doesn't exist
    enabled: isPlatformAdmin, // Only fetch if platform admin
  })

  // Determine system status from metrics
  const systemStatus = metrics?.uptime && metrics.uptime >= 99 ? "healthy" : 
                      metrics?.uptime && metrics.uptime >= 95 ? "degraded" : 
                      "critical"
  
  // Determine pipeline status
  const pipelineStatus = pipelineMetrics?.successRate >= 95 ? "healthy" :
                        pipelineMetrics?.successRate >= 85 ? "degraded" :
                        "critical"

  return (
    <div className="min-h-screen bg-surface pb-20">
      <div className="max-w-admin mx-auto px-admin-content-padding py-8">
        {/* Hero Section */}
        <SectionHero
          title="Admin Command Center"
          description="Monitor system health, manage users, and oversee platform operations"
          gradient="primary"
          accessory={
            <div className="flex items-center gap-2">
              <StatusPill status={systemStatus} label={systemStatus.charAt(0).toUpperCase() + systemStatus.slice(1)} />
              {isPlatformAdmin && (
                <Badge variant="outline" className="bg-role-platform-admin/10 text-role-platform-admin border-role-platform-admin/20">
                  <Shield className="h-3 w-3 mr-1" />
                  Platform Admin
                </Badge>
              )}
              {isCourseAdmin && (
                <Badge variant="outline" className="bg-role-course-admin/10 text-role-course-admin border-role-course-admin/20">
                  <Building2 className="h-3 w-3 mr-1" />
                  {managedCourses.length} Course{managedCourses.length !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          }
          actions={
            isPlatformAdmin && (
              <Button asChild>
                <Link to="/admin/health">
                  <Activity className="h-4 w-4 mr-2" />
                  View System Health
                </Link>
              </Button>
            )
          }
        />

        {/* System Metrics - Only for platform admins */}
        {isPlatformAdmin && (
          <div className="mt-8">
            <h2 className="text-admin-section font-semibold text-foreground mb-4">System Metrics</h2>
          {metricsLoading ? (
            <StatGrid columns={4}>
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-32" />
              ))}
            </StatGrid>
          ) : metricsError ? (
            <div className="bg-admin-warning/10 border border-admin-warning/20 rounded-lg p-4 text-center">
              <p className="text-sm text-muted-foreground mb-2">
                📊 System metrics unavailable - Edge Functions need to be deployed
              </p>
              <p className="text-xs text-muted-foreground">
                Run: <code className="bg-surface px-2 py-1 rounded">supabase functions deploy admin-metrics-quick</code>
              </p>
            </div>
          ) : (
            <StatGrid columns={4}>
              <MetricCard
                title="Total Users"
                value={metrics?.users.toLocaleString() ?? "0"}
                size="large"
              />
              <MetricCard
                title="System Uptime"
                value={`${metrics?.uptime.toFixed(1) ?? 0}%`}
                description="Last 30 days"
                size="large"
                accentColor={systemStatus === "healthy" ? "success" : systemStatus === "degraded" ? "warning" : "danger"}
              />
              <MetricCard
                title="Avg Response"
                value={`${metrics?.avgResponse ?? 0}ms`}
                description="API response time"
                size="large"
              />
              <MetricCard
                title="Pending Approvals"
                value={metrics?.pendingApprovals ?? 0}
                description="Course claims"
                size="large"
                accentColor={metrics && metrics.pendingApprovals > 0 ? "warning" : "success"}
              />
            </StatGrid>
          )}
          </div>
        )}

        {/* Pipeline Metrics - Only for platform admins */}
        {isPlatformAdmin && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-admin-section font-semibold text-foreground">Video Pipeline & AI Coaching</h2>
              <div className="flex items-center gap-2">
                <StatusPill 
                  status={pipelineStatus} 
                  label={`Pipeline ${pipelineStatus.charAt(0).toUpperCase() + pipelineStatus.slice(1)}`} 
                />
                {failedJobs && failedJobs.length > 0 && (
                  <Badge variant="outline" className="bg-admin-warning/10 text-admin-warning border-admin-warning/20">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    {failedJobs.length} Failed Jobs
                  </Badge>
                )}
              </div>
            </div>
            {pipelineLoading ? (
              <StatGrid columns={4}>
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-32" />
                ))}
              </StatGrid>
            ) : pipelineError ? (
              <div className="bg-admin-warning/10 border border-admin-warning/20 rounded-lg p-4 text-center">
                <p className="text-sm text-muted-foreground mb-2">
                  🎥 Pipeline metrics unavailable - Database connection issue
                </p>
              </div>
            ) : (
              <StatGrid columns={4}>
                <MetricCard
                  title="Videos Processed"
                  value={pipelineMetrics?.total.toLocaleString() ?? "0"}
                  description="Last 24 hours"
                  size="large"
                />
                <MetricCard
                  title="Success Rate"
                  value={`${pipelineMetrics?.successRate.toFixed(1) ?? 0}%`}
                  description={`${pipelineMetrics?.completed ?? 0} completed, ${pipelineMetrics?.failed ?? 0} failed`}
                  size="large"
                  accentColor={pipelineStatus === "healthy" ? "success" : pipelineStatus === "degraded" ? "warning" : "danger"}
                />
                <MetricCard
                  title="AI Coaching Rate"
                  value={`${pipelineMetrics?.coachingRate.toFixed(1) ?? 0}%`}
                  description="Videos with coaching results"
                  size="large"
                  accentColor={pipelineMetrics && pipelineMetrics.coachingRate >= 90 ? "success" : "warning"}
                />
                <MetricCard
                  title="Processing Queue"
                  value={pipelineMetrics?.processing ?? 0}
                  description="Currently processing"
                  size="large"
                  accentColor={pipelineMetrics && pipelineMetrics.processing > 10 ? "warning" : "success"}
                />
              </StatGrid>
            )}
          </div>
        )}

        {/* Platform Admin Tools */}
        {isPlatformAdmin && (
          <div className="mt-8">
            <h2 className="text-admin-section font-semibold text-foreground mb-4">Platform Admin Tools</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {platformAdminTools.map((tool) => {
                const Icon = tool.icon
                return (
                  <Link key={tool.path} to={tool.path}>
                    <AdminCard
                      title={tool.title}
                      description={tool.description}
                      accentColor={tool.color}
                      className="h-full hover:shadow-lg transition-shadow cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "p-3 rounded-lg",
                          tool.color === "primary" && "bg-primary/10",
                          tool.color === "success" && "bg-admin-success/10",
                          tool.color === "warning" && "bg-admin-warning/10",
                          tool.color === "info" && "bg-admin-info/10",
                          tool.color === "danger" && "bg-admin-danger/10"
                        )}>
                          <Icon className={cn(
                            "h-6 w-6",
                            tool.color === "primary" && "text-primary",
                            tool.color === "success" && "text-admin-success",
                            tool.color === "warning" && "text-admin-warning",
                            tool.color === "info" && "text-admin-info",
                            tool.color === "danger" && "text-admin-danger"
                          )} />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-muted-foreground">Go to {tool.title} →</p>
                        </div>
                      </div>
                    </AdminCard>
                  </Link>
                )
              })}
            </div>
          </div>
        )}

        {/* Course Admin Tools */}
        {isCourseAdmin && (
          <div className="mt-8">
            <h2 className="text-admin-section font-semibold text-foreground mb-4">Course Admin Tools</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {courseAdminTools.map((tool) => {
                const Icon = tool.icon
                return (
                  <Link key={tool.path} to={tool.path}>
                    <AdminCard
                      title={tool.title}
                      description={tool.description}
                      accentColor={tool.color}
                      className="h-full hover:shadow-lg transition-shadow cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "p-3 rounded-lg",
                          tool.color === "primary" && "bg-primary/10",
                          tool.color === "success" && "bg-admin-success/10",
                          tool.color === "warning" && "bg-admin-warning/10",
                          tool.color === "info" && "bg-admin-info/10",
                          tool.color === "danger" && "bg-admin-danger/10"
                        )}>
                          <Icon className={cn(
                            "h-6 w-6",
                            tool.color === "primary" && "text-primary",
                            tool.color === "success" && "text-admin-success",
                            tool.color === "warning" && "text-admin-warning",
                            tool.color === "info" && "text-admin-info",
                            tool.color === "danger" && "text-admin-danger"
                          )} />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-muted-foreground">Go to {tool.title} →</p>
                        </div>
                      </div>
                    </AdminCard>
                  </Link>
                )
              })}
              {/* Add links to managed courses */}
              {managedCourses.map((course) => (
                <Link key={course.id} to={`/admin/courses/${course.id}`}>
                  <AdminCard
                    title={course.name}
                    description="Manage course settings, posts, and challenges"
                    accentColor="primary"
                    className="h-full hover:shadow-lg transition-shadow cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-3 rounded-lg bg-primary/10">
                        <Building2 className="h-6 w-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-muted-foreground">Manage Course →</p>
                      </div>
                    </div>
                  </AdminCard>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Recent Failed Jobs - Only for platform admins */}
        {isPlatformAdmin && failedJobs && failedJobs.length > 0 && (
          <div className="mt-8">
            <h2 className="text-admin-section font-semibold text-foreground mb-4">Recent Pipeline Failures</h2>
            <div className="bg-admin-danger/5 border border-admin-danger/20 rounded-lg p-4">
              <div className="space-y-3">
                {failedJobs.slice(0, 5).map((job, index) => (
                  <div key={index} className="flex items-center justify-between py-2 border-b border-admin-danger/10 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-admin-danger/10">
                        <AlertTriangle className="h-4 w-4 text-admin-danger" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{job.job_type}</p>
                        <p className="text-xs text-muted-foreground">{job.error_code}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">
                        {new Date(job.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-admin-danger/10">
                <Link to="/admin/debug">
                  <Button variant="outline" size="sm" className="text-admin-danger border-admin-danger/20 hover:bg-admin-danger/10">
                    <Bug className="h-4 w-4 mr-2" />
                    View Debug Tools
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Recent Activity - Only for platform admins */}
        {isPlatformAdmin && (
          <div className="mt-8">
            {activityError ? (
              <div className="bg-admin-warning/10 border border-admin-warning/20 rounded-lg p-6 text-center">
                <h3 className="text-admin-section font-semibold mb-2">Activity Feed Unavailable</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Edge Functions need to be deployed
                </p>
                <p className="text-xs text-muted-foreground">
                  Run: <code className="bg-surface px-2 py-1 rounded">supabase functions deploy admin-activity-recent</code>
                </p>
              </div>
            ) : (
              <ActivityFeed
                items={activity?.items ?? []}
                isLoading={activityLoading}
                emptyMessage="No recent activity"
                title="Recent Activity"
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

